import Reveal from "@/components/Reveal";
import { Award, Users, HeartHandshake } from "lucide-react";

const TRAITS = [
  { icon: Award, title: "Faixa-Preta", text: "Graduado como faixa-preta de Jiu-Jitsu pela equipe ZR Team Sorocaba, Marciano Leme dirige o projeto liderando toda a parte técnica das aulas ministradas, certificando e validando as graduações dos alunos." },
  { icon: Users, title: "Educador", text: "Forma não apenas lutadores, mas cidadãos — cada aula é uma lição de caráter." },
  { icon: HeartHandshake, title: "Idealizador", text: "Fundador do Projeto Bonsai, mantém as portas abertas e gratuitas para a comunidade." },
];

const TEAM = [
  { name: "Marciano Leme", role: "Responsável Técnico · Liderança Estratégica", photo: "/assets/prof-marciano.jpeg", slug: "marciano-leme" },
  { name: "Heitor Rocha", role: "Instrutor Técnico", photo: "/assets/heitor-rocha.jpeg", slug: "heitor-rocha" },
  { name: "Nilton Souza", role: "Instrutor Técnico", photo: "/assets/nilton.jpeg", slug: "nilton" },
  { name: "Marcelo Patriarca", role: "Instrutor Técnico · Liderança Estratégica", photo: "/assets/marcelo-patriarca.jpeg", slug: "marcelo-patriarca" },
  { name: "Suelen Patriarca", role: "Instrutora Técnica · Liderança Estratégica · Tesoureira", photo: "/assets/suelen-patriarca.jpeg", slug: "suelen-patriarca" },
  { name: "Vinicius Souza", role: "Instrutor Técnico", photo: "/assets/vinicius-souza.jpeg", slug: "vinicius-souza" },
  { name: "Jenyffer Leme", role: "Liderança Estratégica · Direção de Impacto Psicológico e Social", photo: "/assets/jenyffer-leme.jpeg", slug: "jenyffer-leme" },
  { name: "Isabelle Rocha", role: "Liderança Estratégica", photo: "/assets/isabelle-rocha.jpeg", slug: "isabelle-rocha" },
  { name: "Melissa Dias", role: "Liderança Estratégica", photo: "/assets/melissa-dias.jpeg", slug: "melissa-dias" },
];

const initials = (name) =>
  name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

export default function Leadership() {
  return (
    <section id="lideranca" className="relative py-28 sm:py-36 bg-surface/30 border-y border-white/5" data-testid="leadership-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <figure className="group relative rounded-2xl overflow-hidden border border-white/10 max-w-md" data-testid="leadership-photo">
            <div aria-hidden="true" className="absolute -inset-6 rounded-full opacity-40" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.25) 0%, transparent 65%)" }} />
            <img
              src="/assets/prof-marciano.jpeg"
              alt="Prof. Marciano de quimono preto com o patch do Projeto Bonsai BJJ"
              loading="lazy"
              className="relative w-full aspect-[4/5] object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
            <figcaption className="absolute bottom-5 left-5">
              <p className="font-mono text-xs tracking-[0.25em] text-gold">LIDERANÇA</p>
              <p className="font-display text-2xl font-bold mt-1">Prof. Marciano</p>
            </figcaption>
          </figure>
        </Reveal>

        <div>
          <Reveal>
            <p className="font-mono text-xs tracking-[0.3em] text-gold mb-4" data-testid="leadership-eyebrow">LIDERANÇA</p>
            <blockquote className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug" data-testid="leadership-quote">
              “O tatame é onde formamos não só lutadores,{" "}
              <span className="text-gold">mas pessoas melhores.</span>”
            </blockquote>
            <p className="mt-6 text-zinc-400 leading-relaxed max-w-lg">
              À frente do Projeto Bonsai, o Prof. Marciano dedica suas noites de segunda e quinta
              a ensinar Jiu-Jitsu gratuitamente para crianças e adultos da comunidade Além Ponte.
              Sua missão: que nenhum aluno deixe de treinar por falta de condições.
            </p>
          </Reveal>

          <div className="mt-10 space-y-5">
            {TRAITS.map((t, i) => (
              <Reveal key={t.title} delay={0.1 + i * 0.1}>
                <div className="flex gap-4 items-start group" data-testid={`leadership-trait-${i}`}>
                  <div className="shrink-0 w-11 h-11 rounded-full border border-gold/30 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-obsidian transition-all duration-300">
                    <t.icon size={20} />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-lg">{t.title}</h3>
                    <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{t.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-24">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.3em] text-gold mb-4" data-testid="team-eyebrow">EQUIPE</p>
          <h3 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight" data-testid="team-title">
            A liderança que cultiva <span className="text-gold">cada aluno.</span>
          </h3>
        </Reveal>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {TEAM.map((m, i) => (
            <Reveal key={m.slug} delay={(i % 4) * 0.1}>
              <article
                data-testid={`leader-card-${m.slug}`}
                className="group h-full bg-obsidian border border-white/10 hover:border-gold/40 rounded-2xl p-6 flex flex-col items-center text-center transition-all duration-500 hover:bg-[#1A1A20] hover:shadow-[0_0_36px_rgba(212,175,55,0.08)] hover:-translate-y-1"
              >
                {m.photo ? (
                  <img
                    src={m.photo}
                    alt={`Foto de ${m.name}`}
                    loading="lazy"
                    className="w-24 h-24 rounded-full object-cover object-top ring-2 ring-gold/50 group-hover:ring-gold group-hover:scale-105 transition-all duration-500"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gold/10 border border-gold/40 flex items-center justify-center group-hover:bg-gold/20 group-hover:scale-105 transition-all duration-500" aria-hidden="true">
                    <span className="font-display text-2xl font-bold text-gold">{initials(m.name)}</span>
                  </div>
                )}
                <h4 className="font-display font-semibold mt-4">{m.name}</h4>
                <p className="font-mono text-[11px] tracking-wider text-zinc-500 mt-1.5 leading-relaxed">{m.role.toUpperCase()}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
